import { TokenManager, authenticatedFetch } from "@/supabase/supabaseClient";
import type { BasemapConfig } from "@/types/layers";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

export interface BasemapFormData {
  key: string;
  label: string;
  type: "raster" | "vector";
  style_url: string;
  attribution?: string;
  is_visible?: boolean;
  display_order?: number;
}

/**
 * Check if current user has admin role
 */
async function checkAdminRole(): Promise<boolean> {
  try {
    // Ensure we have a token
    const tokenReady = await TokenManager.ensureTokenReady();
    if (!tokenReady) {
      console.error(
        "[BasemapService] Token not ready - user likely not authenticated"
      );
      return false;
    }

    // Try to get stored user info first
    let user = TokenManager.getStoredUser();

    if (!user) {
      // Fallback: decode from token
      const token = TokenManager.getStoredToken();
      if (token) {
        user = TokenManager.getUserFromToken(token);
      }
    }

    if (!user) {
      console.error("[BasemapService] No authenticated user found in storage");
      return false;
    }

    // user info available in storage

    // Query user_roles via REST API so that the same auth headers are used as other calls
    const response = await authenticatedFetch(
      `${supabaseUrl}/rest/v1/user_roles?select=role_id,roles(name)&user_id=eq.${user.id}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[BasemapService] Error fetching user roles:", errorText);
      return false;
    }

    const userRoles = await response.json();
    const isAdmin =
      Array.isArray(userRoles) &&
      userRoles.some(
        (role: { roles?: { name?: string } }) =>
          role?.roles?.name?.toLowerCase() === "admin"
      );

    return isAdmin;
  } catch (error) {
    console.error("[BasemapService] Error in checkAdminRole:", error);
    return false;
  }
}

export class BasemapService {
  /**
   * Fetch all basemaps
   */
  static async getAllBasemaps(): Promise<BasemapConfig[]> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/basemaps?select=*&order=display_order.asc.nullslast,created_at.asc`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching basemaps:", error);
        throw new Error(`Failed to fetch basemaps: ${response.statusText}`);
      }

      const data = await response.json();

      // Transform database format to BasemapConfig format
      return data.map(
        (item: {
          key: string;
          label: string;
          type: "raster" | "vector";
          style_url: string;
          attribution: string | null;
          is_visible: boolean;
          display_order: number | null;
        }) => ({
          key: item.key,
          label: item.label,
          type: item.type,
          styleUrl: item.style_url,
          attribution: item.attribution || "",
          isVisible: item.is_visible ?? true,
          displayOrder: item.display_order ?? undefined,
        })
      ) as BasemapConfig[];
    } catch (error) {
      console.error("Error in getAllBasemaps:", error);
      throw error;
    }
  }

  /**
   * Get basemap by key
   */
  static async getBasemapByKey(key: string): Promise<BasemapConfig | null> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/basemaps?select=*&key=eq.${key}`
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error fetching basemap by key:", error);
        return null;
      }

      const data = await response.json();
      if (!data || data.length === 0) return null;

      const item = data[0];
      return {
        key: item.key,
        label: item.label,
        type: item.type,
        styleUrl: item.style_url,
        attribution: item.attribution || "",
        isVisible: item.is_visible ?? true,
        displayOrder: item.display_order,
      };
    } catch (error) {
      console.error("Error in getBasemapByKey:", error);
      return null;
    }
  }

  /**
   * Create a new basemap
   */
  static async createBasemap(basemapData: BasemapFormData): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/basemaps`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Prefer: "return=minimal",
          },
          body: JSON.stringify(basemapData),
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error creating basemap:", error);
        throw new Error(`Failed to create basemap: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error in createBasemap:", error);
      throw error;
    }
  }

  /**
   * Update an existing basemap
   */
  static async updateBasemap(
    key: string,
    basemapData: Partial<BasemapFormData>
  ): Promise<void> {
    try {
      // Check admin role first
      const isAdmin = await checkAdminRole();
      if (!isAdmin) {
        console.error(
          `[BasemapService] User is not admin - update will fail due to RLS`
        );
        throw new Error("You must be an admin to update basemaps");
      }

      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const requestUrl = `${supabaseUrl}/rest/v1/basemaps?key=eq.${key}`;
      const requestBody = JSON.stringify(basemapData);

      // Verify the basemap exists before updating
      const checkResponse = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/basemaps?select=*&key=eq.${key}`
      );
      const existingBasemaps = await checkResponse.json();
      if (!Array.isArray(existingBasemaps) || existingBasemaps.length === 0) {
        console.warn(
          `No basemap found with key='${key}' - update will not match any rows`
        );
      }

      const response = await authenticatedFetch(requestUrl, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Prefer: "return=representation", // Changed to get data back
        },
        body: requestBody,
      });

      const responseText = await response.text();

      if (!response.ok) {
        console.error(`[BasemapService] ❌ Update failed`);
        throw new Error(
          `Failed to update basemap: ${response.statusText} - ${responseText}`
        );
      }

      // Parse response to verify what was actually updated
      try {
        const updatedData = responseText ? JSON.parse(responseText) : null;
        if (
          !updatedData ||
          !Array.isArray(updatedData) ||
          updatedData.length === 0
        ) {
          console.warn(
            "No data returned from update - this might mean no rows were matched"
          );
        }
      } catch (parseError) {
        console.error(`[BasemapService] Could not parse response:`, parseError);
      }

      // end
    } catch (error) {
      console.error(`[BasemapService] ❌ Error in updateBasemap:`, error);
      throw error;
    }
  }

  /**
   * Delete a basemap
   */
  static async deleteBasemap(key: string): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      const response = await authenticatedFetch(
        `${supabaseUrl}/rest/v1/basemaps?key=eq.${key}`,
        {
          method: "DELETE",
          headers: {
            Prefer: "return=minimal",
          },
        }
      );

      if (!response.ok) {
        const error = await response.text();
        console.error("Error deleting basemap:", error);
        throw new Error(`Failed to delete basemap: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error in deleteBasemap:", error);
      throw error;
    }
  }

  /**
   * Update basemap visibility
   */
  static async updateBasemapVisibility(
    key: string,
    isVisible: boolean
  ): Promise<void> {
    await this.updateBasemap(key, { is_visible: isVisible });
  }

  /**
   * Reorder basemaps
   */
  static async reorderBasemaps(
    updates: Array<{ key: string; display_order: number }>
  ): Promise<void> {
    try {
      const tokenReady = await TokenManager.ensureTokenReady();
      if (!tokenReady) {
        throw new Error("Authentication token not available");
      }

      // Update each basemap's display_order
      const updatePromises = updates.map(({ key, display_order }) =>
        this.updateBasemap(key, { display_order })
      );

      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error in reorderBasemaps:", error);
      throw error;
    }
  }
}
